import { useEffect, useRef, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { useLang } from "@/contexts/LangContext";
import { useAuth } from "@/contexts/AuthContext";
import {
  PawPrint,
  Dog,
  Cat,
  Plus,
  FileText,
  FilePdf,
  Paperclip,
  Download,
  Trash,
  Pencil,
  Check,
  X,
  ArrowLeft,
  Link as LinkIcon,
  Copy,
  Archive,
  ArrowUUpLeft,
  Camera,
} from "@phosphor-icons/react";
import { toast } from "sonner";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import BirthOrAgeInput from "@/components/BirthOrAgeInput";
import BreedAutocomplete from "@/components/BreedAutocomplete";
import { API_URL } from "@/lib/api";
import { compressImageFile } from "@/lib/imageCompress";
import { formatAge, speciesLabel, sexLabel } from "@/lib/patientUtils";
import { sanitizePhone, isValidEmail } from "@/lib/patientUtils";
import { formatDate } from "@/lib/dateFmt";

function ageFromBirth(iso, approximate = false, lang = "ro") {
  if (!iso) return "—";
  return formatAge(iso, { lang, approximate });
}

function speciesIcon(sp) {
  const s = (sp || "").toLowerCase();
  if (s === "cat") return <Cat size={36} weight="fill" className="text-pf-accent" />;
  if (s === "dog") return <Dog size={36} weight="fill" className="text-pf-primary" />;
  return <PawPrint size={36} weight="fill" className="text-pf-text-secondary" />;
}

export default function PatientProfile() {
  const { id } = useParams();
  const { t, lang } = useLang();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [p, setP] = useState(null);
  const [consults, setConsults] = useState([]);
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState(null);
  const [archiveOpen, setArchiveOpen] = useState(false);
  const [imgVersion, setImgVersion] = useState(0);
  const fileRef = useRef(null);
  const photoRef = useRef(null);

  async function load() {
    try {
      const [pRes, cRes] = await Promise.all([
        api.get(`/patients/${id}`),
        api.get("/consultations", { params: { patient_id: id, limit: 100 } }),
      ]);
      setP(pRes.data);
      setForm(pRes.data);
      setConsults(cRes.data);
    } catch (e) {
      toast.error(formatApiError(e));
    }
  }
  useEffect(() => {
    load();
    // eslint-disable-next-line
  }, [id]);

  async function newConsultation() {
    try {
      const { data } = await api.post("/consultations", { patient_id: id });
      navigate(`/consultations/${data.id}`);
    } catch (e) {
      toast.error(formatApiError(e));
    }
  }

  async function deletePatient() {
    if (!window.confirm(`Delete ${p.name} and all their consultations? This cannot be undone.`)) return;
    try {
      await api.delete(`/patients/${id}`);
      toast.success("Patient deleted");
      navigate("/patients");
    } catch (e) {
      toast.error(formatApiError(e));
    }
  }

  async function unarchive() {
    if (!window.confirm(t("unarchiveConfirm"))) return;
    try {
      await api.post(`/patients/${id}/unarchive`);
      toast.success(t("saved"));
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  }

  async function uploadPhoto(e) {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith("image/")) {
      toast.error("Only image files");
      return;
    }
    const fd = new FormData();
    fd.append("file", file);
    try {
      await api.post(`/patients/${id}/profile-image`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success(t("saved"));
      setImgVersion((v) => v + 1);
      load();
      if (photoRef.current) photoRef.current.value = "";
    } catch (err) {
      toast.error(formatApiError(err));
    }
  }

  async function removePhoto() {
    if (!window.confirm(t("removePhoto") + "?")) return;
    try {
      await api.delete(`/patients/${id}/profile-image`);
      setImgVersion((v) => v + 1);
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  }

  async function saveEdit() {
    if (form.owner?.email && !isValidEmail(form.owner.email)) {
      toast.error(t("invalidEmail"));
      return;
    }
    try {
      const body = {
        name: form.name,
        species: form.species,
        breed: form.breed,
        sex: form.sex,
        birth_date: form.birth_date,
        birth_date_approximate: !!form.birth_date_approximate,
        neutered: !!form.neutered,
        owner: form.owner,
      };
      const { data } = await api.patch(`/patients/${id}`, body);
      setP(data);
      setForm(data);
      setEditing(false);
      toast.success(t("saved"));
    } catch (e) {
      toast.error(formatApiError(e));
    }
  }

  async function uploadFile(e) {
    const file = e.target.files?.[0];
    if (!file) return;
    const fd = new FormData();
    fd.append("file", await compressImageFile(file));
    try {
      await api.post(`/patients/${id}/attachments`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success("Uploaded");
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      if (fileRef.current) fileRef.current.value = "";
    }
  }

  async function deleteAttachment(aid) {
    if (!window.confirm("Delete this attachment?")) return;
    try {
      await api.delete(`/patients/${id}/attachments/${aid}`);
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  }

  async function viewAttachment(a) {
    try {
      const res = await api.get(`/patients/${id}/attachments/${a.id}`, { responseType: "blob" });
      const blob = new Blob([res.data], { type: a.content_type || "application/octet-stream" });
      const url = URL.createObjectURL(blob);
      const canPreview = (a.content_type || "").startsWith("image/") || a.content_type === "application/pdf";
      if (canPreview) {
        window.open(url, "_blank", "noopener,noreferrer");
      } else {
        const link = document.createElement("a");
        link.href = url;
        link.download = a.filename || "attachment";
        document.body.appendChild(link);
        link.click();
        link.remove();
      }
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch (e) {
      toast.error(formatApiError(e));
    }
  }

  async function viewConsultPdf(cid) {
    // Open the consultation PDF directly in a new tab (browser PDF viewer) —
    // no download required. iOS Safari blocks window.open() after an await, so
    // pre-open the tab synchronously.
    const ua = window.navigator.userAgent || "";
    const isIOS = /iPad|iPhone|iPod/.test(ua) && !window.MSStream;
    const win = window.open("", "_blank");
    try {
      const res = await api.get(`/consultations/${cid}/pdf`, { params: { lang }, responseType: "blob" });
      const url = URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      if (win) win.location = url;
      else window.location.href = url;
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch (e) {
      if (win) win.close();
      toast.error(formatApiError(e));
    }
  }

  if (!p) return <div className="text-pf-text-secondary text-sm">Loading…</div>;

  return (
    <div className="animate-fade-in-up">
      <Link to="/patients" className="inline-flex items-center gap-1 text-sm text-pf-text-secondary hover:text-pf-text mb-4">
        <ArrowLeft size={14} /> {t("patients")}
      </Link>

      <div className="pf-card p-6 mb-5">
        {p.archived && (
          <div className="mb-4 rounded-lg border border-amber-400 bg-amber-50 text-amber-900 p-3 text-sm flex items-start gap-2" data-testid="archived-banner">
            <Archive size={16} className="mt-0.5" />
            <div className="flex-1">
              <div className="font-semibold">{t("archived")}</div>
              {p.archive_reason && (
                <div className="text-xs">
                  {t("archiveReasonLabel")}: {p.archive_reason}
                  {p.archive_note ? ` — ${p.archive_note}` : ""}
                </div>
              )}
            </div>
            <button onClick={unarchive} className="pf-btn-ghost !py-1 !px-2 text-xs" data-testid="unarchive-btn">
              <ArrowUUpLeft size={13} /> {t("unarchive")}
            </button>
          </div>
        )}
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div className="flex items-center gap-4">
            <div className="relative group">
              {p.profile_image ? (
                <img
                  src={`${API_URL}/patients/${id}/profile-image?v=${imgVersion}`}
                  alt={p.name}
                  data-testid="patient-photo"
                  className="h-20 w-20 rounded-xl object-cover shadow-card"
                />
              ) : (
                <div className="h-20 w-20 rounded-xl bg-pf-primary-light grid place-items-center">
                  {speciesIcon(p.species)}
                </div>
              )}
              <input ref={photoRef} type="file" accept="image/*" hidden onChange={uploadPhoto} data-testid="patient-photo-input" />
              <button
                type="button"
                onClick={() => photoRef.current?.click()}
                className="absolute -bottom-1 -right-1 h-7 w-7 rounded-full bg-pf-primary text-white grid place-items-center shadow-card hover:brightness-110 transition"
                title={t("changePhoto")}
                data-testid="patient-photo-btn"
              >
                <Camera size={13} weight="fill" />
              </button>
              {p.profile_image && (
                <button
                  type="button"
                  onClick={removePhoto}
                  className="absolute -top-2 -right-2 h-6 w-6 rounded-full bg-white border border-pf-border text-pf-danger grid place-items-center shadow-card opacity-0 group-hover:opacity-100 transition"
                  title={t("removePhoto")}
                  data-testid="patient-photo-remove"
                >
                  <X size={11} />
                </button>
              )}
            </div>
            <div>
              <div className="flex items-center gap-2 flex-wrap">
                <h1 className="text-3xl font-display font-extrabold tracking-tight text-pf-text">
                  {p.name}
                </h1>
                <span
                  className="font-mono text-xs text-pf-text-secondary"
                  data-testid="patient-profile-id"
                >
                  ID #{p.seq ?? "—"}
                </span>
                <span className="pf-chip-primary">{speciesLabel(p.species, t)}</span>
                {p.neutered && <span className="pf-chip">{t("neutered")}</span>}
              </div>
              <div className="text-sm text-pf-text-secondary mt-1">
                {p.breed || "—"} · {sexLabel(p.sex, t)} · {t("age")}: {ageFromBirth(p.birth_date, p.birth_date_approximate, lang)}
              </div>
            </div>
          </div>
          <div className="flex gap-2 flex-wrap">
            {!editing ? (
              <button data-testid="edit-patient-btn" onClick={() => setEditing(true)} className="pf-btn-ghost">
                <Pencil size={14} /> Edit
              </button>
            ) : (
              <>
                <button onClick={() => { setEditing(false); setForm(p); }} className="pf-btn-ghost">
                  <X size={14} /> {t("cancel")}
                </button>
                <button data-testid="save-patient-btn" onClick={saveEdit} className="pf-btn-primary">
                  <Check size={14} /> {t("save")}
                </button>
              </>
            )}
            <button data-testid="new-consult-from-patient" onClick={newConsultation} className="pf-btn-accent">
              <Plus size={14} weight="bold" /> {t("newConsultation")}
            </button>
            {!p.archived ? (
              <button
                onClick={() => setArchiveOpen(true)}
                data-testid="archive-patient-btn"
                className="pf-btn-ghost !text-pf-warning !border-pf-warning/30 hover:!bg-pf-warning-bg"
                title={t("archive")}
              >
                <Archive size={14} />
              </button>
            ) : (
              <button
                onClick={unarchive}
                data-testid="unarchive-patient-btn-toolbar"
                className="pf-btn-ghost !text-pf-primary !border-pf-primary/30 hover:!bg-pf-primary-light"
                title={t("unarchive")}
              >
                <ArrowUUpLeft size={14} />
              </button>
            )}
            <button
              data-testid="delete-patient-btn"
              onClick={deletePatient}
              className="pf-btn-ghost !text-pf-danger !border-pf-danger/30 hover:!bg-pf-danger-bg"
              title={t("deletePatient")}
            >
              <Trash size={14} />
            </button>
          </div>
        </div>

        {/* Edit / view */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-6">
          <div>
            <div className="text-sm font-bold text-pf-text mb-2">{t("newPatient").replace(/^New\s/i, "").replace(/^Pacient\s.*/i, "Pacient")}</div>
            {!editing ? (
              <dl className="text-sm space-y-1.5">
                <Info label={t("species")} value={speciesLabel(p.species, t)} />
                <Info label={t("breed")} value={p.breed || "—"} />
                <Info label={t("sex")} value={sexLabel(p.sex, t)} />
                <Info label={t("birthDate")} value={
                  p.birth_date
                    ? `${formatDate(p.birth_date)} (${ageFromBirth(p.birth_date, p.birth_date_approximate, lang)})`
                    : "—"
                } />
                <Info label={t("neutered")} value={p.neutered ? t("yes") : t("no")} />
              </dl>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <input className="pf-input col-span-2" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
                <select className="pf-input" value={form.species} onChange={(e) => setForm({ ...form, species: e.target.value })}>
                  <option value="dog">{t("dog")}</option>
                  <option value="cat">{t("cat")}</option>
                  <option value="other">{t("other")}</option>
                </select>
                <BreedAutocomplete
                  species={form.species}
                  value={form.breed || ""}
                  onChange={(v) => setForm({ ...form, breed: v })}
                />
                <select className="pf-input" value={form.sex || ""} onChange={(e) => setForm({ ...form, sex: e.target.value })}>
                  <option value="">—</option>
                  <option value="male">{t("male")}</option>
                  <option value="female">{t("female")}</option>
                </select>
                <div className="col-span-2">
                  <BirthOrAgeInput
                    testIdPrefix="edit-birth"
                    value={form.birth_date || ""}
                    approximate={!!form.birth_date_approximate}
                    onChange={({ birth_date, birth_date_approximate }) =>
                      setForm({ ...form, birth_date, birth_date_approximate })
                    }
                  />
                </div>
                <label className="flex items-center gap-2 text-sm col-span-2">
                  <input type="checkbox" checked={!!form.neutered} onChange={(e) => setForm({ ...form, neutered: e.target.checked })} />
                  {t("neutered")}
                </label>
              </div>
            )}
          </div>
          <div>
            <div className="text-sm font-bold text-pf-text mb-2">{t("owner")}</div>
            {!editing ? (
              <dl className="text-sm space-y-1.5">
                <Info label={t("fullName")} value={p.owner?.full_name} />
                <Info label={t("cnp")} value={p.owner?.cnp || "—"} />
                <Info label={t("phone")} value={p.owner?.phone || "—"} />
                <Info label={t("email")} value={p.owner?.email || "—"} />
                <Info label={t("address")} value={p.owner?.address || "—"} />
              </dl>
            ) : (
              <div className="grid grid-cols-1 gap-3">
                <input className="pf-input" placeholder={t("fullName")} value={form.owner?.full_name || ""} onChange={(e) => setForm({ ...form, owner: { ...form.owner, full_name: e.target.value } })} />
                <input className="pf-input" placeholder={t("cnp")} maxLength={13} value={form.owner?.cnp || ""} onChange={(e) => setForm({ ...form, owner: { ...form.owner, cnp: e.target.value.replace(/[^\d]/g, "") } })} />
                <input className="pf-input" inputMode="tel" placeholder={t("phone")} value={form.owner?.phone || ""} onChange={(e) => setForm({ ...form, owner: { ...form.owner, phone: sanitizePhone(e.target.value) } })} />
                <input className="pf-input" type="email" placeholder={t("email")} value={form.owner?.email || ""} onChange={(e) => setForm({ ...form, owner: { ...form.owner, email: e.target.value } })} />
                <input className="pf-input" placeholder={t("address")} value={form.owner?.address || ""} onChange={(e) => setForm({ ...form, owner: { ...form.owner, address: e.target.value } })} />
              </div>
            )}
          </div>
        </div>
      </div>

      <Tabs defaultValue="consultations">
        <TabsList className="bg-white border border-pf-border rounded-lg p-1">
          <TabsTrigger value="consultations" data-testid="tab-consultations">
            {t("consultations")} ({consults.length})
          </TabsTrigger>
          <TabsTrigger value="attachments" data-testid="tab-attachments">
            {t("attachments")} ({p.attachments?.length || 0})
          </TabsTrigger>
        </TabsList>

        <TabsContent value="consultations" className="mt-4">
          <div className="pf-card overflow-hidden">
            {consults.length === 0 ? (
              <div className="p-8 text-center text-sm text-pf-text-secondary">
                {t("noResults")}
              </div>
            ) : (
              <div className="divide-y divide-pf-border">
                {consults.map((c) => (
                  <div
                    key={c.id}
                    data-testid={`consult-item-${c.id}`}
                    className="flex items-center gap-4 px-5 py-3 hover:bg-pf-bg transition-colors"
                  >
                    <Link to={`/consultations/${c.id}`} className="flex items-center gap-4 flex-1 min-w-0">
                      <div className="h-9 w-9 rounded-full bg-pf-primary-light grid place-items-center shrink-0">
                        <FileText size={16} className="text-pf-primary" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="font-semibold text-pf-text">
                            {formatDate(c.created_at)}
                          </span>
                          {c.status === "draft" ? (
                            <span className="pf-chip text-pf-warning bg-pf-warning-bg border-pf-warning/30">draft</span>
                          ) : (
                            <span className="pf-chip-primary">final</span>
                          )}
                        </div>
                        <div className="text-xs text-pf-text-secondary truncate">
                          {(c.visit_reasons || []).join(", ") || "—"} · {c.attending_vet_name || "—"}
                        </div>
                        {c.diagnosis && (
                          <div className="text-xs text-pf-text-secondary truncate mt-0.5">
                            Dx: {c.diagnosis}
                          </div>
                        )}
                      </div>
                    </Link>
                    <button
                      type="button"
                      onClick={() => viewConsultPdf(c.id)}
                      data-testid={`consult-view-pdf-${c.id}`}
                      className="pf-btn-ghost !py-1.5 shrink-0"
                      title={t("view")}
                    >
                      <FilePdf size={14} /> <span className="hidden sm:inline">PDF</span>
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>
        </TabsContent>

        <TabsContent value="attachments" className="mt-4">
          <div className="pf-card p-5">
            <div className="flex items-center justify-between mb-3">
              <div className="pf-section-title">{t("attachments")}</div>
              <div>
                <input ref={fileRef} type="file" hidden onChange={uploadFile} data-testid="attach-file-input" />
                <button data-testid="attach-file-btn" onClick={() => fileRef.current?.click()} className="pf-btn-primary">
                  <Paperclip size={14} /> {t("addAttachment")}
                </button>
              </div>
            </div>
            {(!p.attachments || p.attachments.length === 0) ? (
              <div className="text-sm text-pf-text-secondary py-6 text-center">
                {t("noResults")}
              </div>
            ) : (
              <ul className="divide-y divide-pf-border">
                {p.attachments.map((a) => (
                  <li key={a.id} className="flex items-center gap-3 py-3">
                    <Paperclip size={16} className="text-pf-text-secondary" />
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-medium truncate">{a.filename}</div>
                      <div className="text-xs text-pf-text-secondary">
                        {(a.size / 1024).toFixed(1)} KB · {a.content_type}
                      </div>
                    </div>
                    <button
                      type="button"
                      onClick={() => viewAttachment(a)}
                      className="pf-btn-ghost !py-1.5"
                      title={t("view") || "View"}
                    >
                      <Download size={14} />
                    </button>
                    <button onClick={() => deleteAttachment(a.id)} className="pf-btn-ghost !py-1.5 text-pf-danger border-pf-danger/30 hover:bg-pf-danger-bg">
                      <Trash size={14} />
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </TabsContent>

        <TabsContent value="portal" className="mt-4">
          <PortalPanel patientId={id} patient={p} onUpdated={load} />
        </TabsContent>
      </Tabs>

      <ArchiveDialog
        open={archiveOpen}
        onOpenChange={setArchiveOpen}
        patientId={id}
        onDone={() => { setArchiveOpen(false); load(); }}
      />
    </div>
  );
}

function ArchiveDialog({ open, onOpenChange, patientId, onDone }) {
  const { t } = useLang();
  const [reason, setReason] = useState("deceased");
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open) {
      setReason("deceased");
      setNote("");
    }
  }, [open]);

  async function submit(e) {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post(`/patients/${patientId}/archive`, { reason, note });
      toast.success(t("saved"));
      onDone();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy(false);
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle className="font-display flex items-center gap-2">
            <Archive size={18} className="text-pf-warning" />
            {t("archivePatient")}
          </DialogTitle>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-3">
          <div>
            <label className="pf-label mb-1 block">{t("archiveReasonLabel")}</label>
            <select
              data-testid="archive-reason"
              className="pf-input"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
            >
              <option value="deceased">{t("archiveReasonDeceased")}</option>
              <option value="moved">{t("archiveReasonMoved")}</option>
              <option value="rejected">{t("archiveReasonRejected")}</option>
              <option value="other">{t("archiveReasonOther")}</option>
            </select>
          </div>
          <div>
            <label className="pf-label mb-1 block">{t("archiveNote")}</label>
            <textarea
              data-testid="archive-note"
              rows={3}
              className="pf-input min-h-[80px]"
              value={note}
              onChange={(e) => setNote(e.target.value)}
            />
          </div>
          <div className="flex justify-end gap-2 pt-2">
            <button type="button" onClick={() => onOpenChange(false)} className="pf-btn-ghost">
              {t("cancel")}
            </button>
            <button data-testid="archive-submit" type="submit" disabled={busy} className="pf-btn-primary">
              {busy ? "…" : t("archiveConfirm")}
            </button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function Info({ label, value, capitalize }) {
  return (
    <div className="flex gap-3">
      <dt className="w-28 pf-label pt-0.5">{label}</dt>
      <dd className={`text-pf-text ${capitalize ? "capitalize" : ""}`}>{value}</dd>
    </div>
  );
}

function PortalPanel({ patientId, patient, onUpdated }) {
  const [busy, setBusy] = useState(false);
  const portalUrl = patient?.portal_token
    ? `${window.location.origin}/portal/${patient.portal_token}`
    : "";

  async function generate() {
    setBusy(true);
    try {
      const { data } = await api.post(`/patients/${patientId}/portal-token`);
      toast.success("Portal link ready");
      if (data.portal_url) {
        try {
          await navigator.clipboard.writeText(`${window.location.origin}/portal/${data.portal_token}`);
        } catch {}
      }
      onUpdated();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  }

  async function revoke() {
    if (!window.confirm("Revoke this portal link?")) return;
    try {
      await api.delete(`/patients/${patientId}/portal-token`);
      toast.success("Portal link revoked");
      onUpdated();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  }

  async function copyLink() {
    try {
      await navigator.clipboard.writeText(portalUrl);
      toast.success("Link copied");
    } catch {
      toast.error("Copy failed");
    }
  }

  return (
    <div className="pf-card p-5">
      <div className="flex items-center gap-2 mb-2">
        <LinkIcon size={16} className="text-pf-primary" />
        <div className="pf-section-title">Owner portal</div>
      </div>
      <p className="text-sm text-pf-text-secondary mb-4">
        Generate a private link the pet's owner can use to view and download every consultation report — no login required.
      </p>
      {portalUrl ? (
        <div className="space-y-3">
          <div className="flex items-center gap-2 rounded-lg bg-pf-bg border border-pf-border px-3 py-2 text-sm font-mono break-all">
            <span className="flex-1 truncate" data-testid="portal-url">{portalUrl}</span>
            <button onClick={copyLink} data-testid="copy-portal-btn" className="pf-btn-ghost !py-1 !px-2">
              <Copy size={14} />
            </button>
          </div>
          <div className="flex gap-2">
            <a href={portalUrl} target="_blank" rel="noreferrer" className="pf-btn-ghost">
              Preview
            </a>
            <button onClick={generate} disabled={busy} className="pf-btn-ghost" data-testid="regenerate-portal-btn">
              Regenerate
            </button>
            <button onClick={revoke} className="pf-btn-ghost text-pf-danger border-pf-danger/30 hover:bg-pf-danger-bg">
              Revoke
            </button>
          </div>
        </div>
      ) : (
        <button onClick={generate} disabled={busy} className="pf-btn-primary" data-testid="generate-portal-btn">
          <LinkIcon size={14} /> {busy ? "Generating…" : "Generate portal link"}
        </button>
      )}
    </div>
  );
}
